import {
  type ExtractDocumentTypeFromTypedRxJsonSchema,
  type RxCollection,
  type RxJsonSchema,
  toTypedRxJsonSchema,
} from "rxdb";
import { useRxCollection } from "rxdb-hooks";

export const RXDB_NOTIFICATION_COLLECTION_NAME = "notification";

export const useRxCollectionNotification =
  (): RxCollection<Notification> | null =>
    useRxCollection<Notification>(RXDB_NOTIFICATION_COLLECTION_NAME);

const NOTIFICATION_SCHEMA_LITERAL = {
  title: "notification schema",
  version: 2,
  description:
    "Local-only bell row emitted by a producer adapter (ADR-004). Re-derived on every fresh login.",
  type: "object",
  primaryKey: "id",
  properties: {
    id: {
      // `${userId}|${key}` — deterministic so emit upsert is atomic by
      // (userId, key). userId (100) + "|" (1) + key (200) = up to 301, with
      // a small cushion for the rare future change.
      type: "string",
      maxLength: 320,
    },
    userId: {
      type: "string",
      maxLength: 100,
    },
    key: {
      type: "string",
      maxLength: 200,
    },
    title: {
      type: "string",
    },
    body: {
      type: ["string", "null"],
    },
    severity: {
      type: "string",
      enum: ["ACTION_REQUIRED", "INFO"],
      maxLength: 16,
    },
    category: {
      type: "string",
      maxLength: 100,
    },
    icon: {
      type: ["string", "null"],
    },
    actionUrl: {
      type: ["string", "null"],
    },
    version: {
      type: ["string", "null"],
    },
    createdAt: {
      type: "string",
      format: "date-time",
      maxLength: 30,
    },
    readAt: {
      type: ["string", "null"],
      format: "date-time",
    },
    expiresAt: {
      type: ["string", "null"],
      format: "date-time",
    },
  },
  required: [
    "id",
    "userId",
    "key",
    "title",
    "severity",
    "category",
    "createdAt",
  ],
  indexes: ["userId", ["userId", "key"]],
  /*
   * It is important to set 'as const' to preserve the literal type.
   *
   * biome-ignore lint/suspicious/noExplicitAny: We infer the DB type from this
   * object, the satisfies just provides us type inference for this object.
   */
} as const satisfies RxJsonSchema<any>;

const schemaTyped = toTypedRxJsonSchema(NOTIFICATION_SCHEMA_LITERAL);

export type Notification = ExtractDocumentTypeFromTypedRxJsonSchema<
  typeof schemaTyped
>;

export const NOTIFICATION_SCHEMA: RxJsonSchema<Notification> =
  NOTIFICATION_SCHEMA_LITERAL;
